import { API_BASE_URL } from '../config';
import {
  HsmContact,
  HsmContactRequest,
  HsmBulkContactRequest,
  HsmContactFilter
} from '../types';

// ==================== CONTACTS ====================

export interface FetchContactsOptions {
  status?: HsmContactFilter;
  day?: number;
}

export async function fetchContacts(
  accessToken: string,
  options?: FetchContactsOptions
): Promise<HsmContact[]> {
  let endpoint = `${API_BASE_URL}/hsm/contacts`;

  if (options?.day) {
    endpoint = `${API_BASE_URL}/hsm/contacts/day/${options.day}`;
  } else if (options?.status === 'active') {
    endpoint = `${API_BASE_URL}/hsm/contacts/active`;
  }

  const response = await fetch(endpoint, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao carregar contatos');
  }

  const payload = await response.json();
  return payload.data || [];
}

export async function fetchContact(
  contactId: number,
  accessToken: string
): Promise<HsmContact> {
  const response = await fetch(`${API_BASE_URL}/hsm/contacts/${contactId}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao carregar contato');
  }

  const payload = await response.json();
  return payload.data;
}

export async function createContact(
  data: HsmContactRequest,
  accessToken: string
): Promise<HsmContact> {
  const response = await fetch(`${API_BASE_URL}/hsm/contacts`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`
    },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao criar contato');
  }

  const payload = await response.json();
  return payload.data;
}

export async function bulkImportContacts(
  data: HsmBulkContactRequest,
  accessToken: string
): Promise<{ created: number; skipped: number; skippedNumbers: string[] }> {
  const response = await fetch(`${API_BASE_URL}/hsm/contacts/bulk`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`
    },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao importar contatos');
  }

  const payload = await response.json();
  return payload.data;
}

export async function updateContact(
  contactId: number,
  data: Partial<HsmContactRequest>,
  accessToken: string
): Promise<HsmContact> {
  const response = await fetch(`${API_BASE_URL}/hsm/contacts/${contactId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`
    },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao atualizar contato');
  }

  const payload = await response.json();
  return payload.data;
}

export async function activateContact(
  contactId: number,
  accessToken: string
): Promise<HsmContact> {
  const response = await fetch(`${API_BASE_URL}/hsm/contacts/${contactId}/activate`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao ativar contato');
  }

  const payload = await response.json();
  return payload.data;
}

export async function deactivateContact(
  contactId: number,
  accessToken: string
): Promise<HsmContact> {
  const response = await fetch(`${API_BASE_URL}/hsm/contacts/${contactId}/deactivate`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao desativar contato');
  }

  const payload = await response.json();
  return payload.data;
}

export async function deleteContact(
  contactId: number,
  accessToken: string
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/hsm/contacts/${contactId}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao excluir contato');
  }
}

// ==================== CSV IMPORT ====================

export interface CsvImportResult {
  created: number;
  duplicates: number;
  skippedNumbers: string[];
  batchId: number;
  batchName: string;
}

export async function importContactsCsv(
  file: File,
  accessToken: string,
  options?: {
    batchId?: number;
    batchName?: string;
  }
): Promise<CsvImportResult> {
  const formData = new FormData();
  formData.append('file', file);

  if (options?.batchId) {
    formData.append('batchId', options.batchId.toString());
  }
  if (options?.batchName) {
    formData.append('batchName', options.batchName);
  }

  const response = await fetch(`${API_BASE_URL}/hsm/contacts/import-csv`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`
    },
    body: formData
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao importar CSV');
  }

  const payload = await response.json();
  return payload.data;
}
